import { DateTime } from 'luxon'
import { computed, type MaybeRefOrGetter, type Ref, toRef } from 'vue'
import useForm from '../useForm'
import ServiceIncreaseComputeType from '../../Constants/ServiceIncreaseComputeType.ts'
import type { Service, ServiceFormDef } from '../../Types/service'

interface ServiceFormArguments {
  service?: MaybeRefOrGetter<Service | null>
  def?: MaybeRefOrGetter<ServiceFormDef | null>
}

export default function useServiceForm({ service: existingService, def: existingDef }: ServiceFormArguments = {}) {
  const service: Ref<Service | null> = toRef(existingService ?? null)
  const { def, isFieldDisplayed }: { def: Ref<ServiceFormDef | null>; isFieldDisplayed: Function } = useForm({
    def: existingDef as Record<string, any>,
  })

  const hasIncreaseRateField = computed<boolean>(
    () => isFieldDisplayed('increaseRate') && service?.value?.increaseComputeType !== ServiceIncreaseComputeType.NONE,
  )

  const disabledStartDates = computed<string[]>(() => {
    const serviceEndAt = service.value?.endAt
    if (!serviceEndAt) {
      return []
    }

    const availableDates = Object.values(def.value?.startAt?.options?.choices ?? {}).filter(
      (date): date is string => typeof date === 'string',
    )

    return availableDates.filter((date) => DateTime.fromISO(date) > DateTime.fromISO(serviceEndAt))
  })

  const disabledEndDates = computed<string[]>(() => {
    const serviceStartAt = service.value?.startAt
    if (!serviceStartAt) {
      return []
    }

    const availableDates = Object.values(def.value?.endAt?.options.choices ?? {}).filter(
      (date): date is string => typeof date === 'string',
    )

    return availableDates.filter((date) => DateTime.fromISO(date) < DateTime.fromISO(serviceStartAt))
  })

  return {
    def,
    hasIncreaseRateField,
    disabledStartDates,
    disabledEndDates,
  }
}
